import React, { useState, useMemo } from "react";
import {
  IconButton,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Psychology as PsychologyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { DatabaseObjectView } from "../DatabaseObjectView";

interface GptRequestsListProps {
  gptRequestIds: string[];
  size?: "small" | "medium" | "large";
}

export function GptRequestsList({
  gptRequestIds,
  size = "small"
}: GptRequestsListProps) {
  const [listOpen, setListOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [objectViewOpen, setObjectViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter GPT requests based on search query
  const filteredRequestIds = useMemo(() => {
    if (!gptRequestIds || gptRequestIds.length === 0) {
      return [];
    }

    if (!searchQuery.trim()) {
      return gptRequestIds;
    }

    const query = searchQuery.toLowerCase().trim();
    return gptRequestIds.filter((id) =>
      id.toLowerCase().includes(query)
    );
  }, [gptRequestIds, searchQuery]);

  if (!gptRequestIds || gptRequestIds.length === 0) {
    return null;
  }

  const handleOpenList = () => {
    setListOpen(true);
  };

  const handleCloseList = () => {
    setListOpen(false);
  };

  const handleRequestClick = (requestId: string) => {
    setSelectedRequestId(requestId);
    setListOpen(false);
    setObjectViewOpen(true);
  };

  const handleCloseObjectView = () => {
    setObjectViewOpen(false);
    setSelectedRequestId(null);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <>
      <Tooltip title={`View ${gptRequestIds.length} GPT request${gptRequestIds.length > 1 ? 's' : ''}`}>
        <IconButton size={size} onClick={handleOpenList}>
          <Badge badgeContent={gptRequestIds.length} color="primary">
            <PsychologyIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* GPT Requests List Dialog */}
      <Dialog open={listOpen} onClose={handleCloseList} maxWidth="sm" fullWidth>
        <DialogTitle>
          GPT Requests ({gptRequestIds.length})
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on a request to view its details.
          </Typography>

          {/* Search Field */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Results count when filtering */}
          {searchQuery && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Showing {filteredRequestIds.length} of {gptRequestIds.length} requests
            </Typography>
          )}

          <List>
            {filteredRequestIds.length > 0 ? (
              filteredRequestIds.map((requestId, index) => {
                // Find original index for display
                const originalIndex = gptRequestIds.indexOf(requestId);
                return (
                  <ListItem key={requestId} disablePadding>
                    <ListItemButton onClick={() => handleRequestClick(requestId)}>
                      <ListItemText
                        primary={`GPT Request ${originalIndex + 1}`}
                        secondary={
                          <Box sx={{ fontFamily: "monospace" }}>
                            {requestId}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No requests match your search
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseList}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Database Object View for selected GPT request */}
      {selectedRequestId && (
        <DatabaseObjectView
          open={objectViewOpen}
          onClose={handleCloseObjectView}
          initialTable="gptRequest"
          initialId={selectedRequestId}
          autoFetch={true}
        />
      )}
    </>
  );
}